// src/App.js
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Navigate, Routes, Route } from "react-router-dom";

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

import Frontoffice from './Pages/Frontoffice';
import FrontOfficeNew from './Pages/FrontOfficeNew';
import FrontOfficeShortcuts from './Pages/FrontOfficeShortcuts';
import Backoffice from './Pages/Backoffice';
import Reports from './Pages/Reports';
import DnisStatisticsReport from './Pages/DnisStatisticsReport';
import DailyCallReport from './Pages/DailyCallReport';
import AgentStateReport from './Pages/AgentStateReport';
import AgentLoginLogoutReport from './Pages/AgentLoginLogoutReport';
import AgentPerformanceReport from './Pages/AgentPerformanceReport';
import AgentCallHistoryReport from './Pages/AgentCallHistoryReport';
import OriginatorDispositionReport from './Pages/OriginatorDispositionReport';
import QueueContactOptionReport from './Pages/QueueContactOptionReport';
import ErrorBoundary from "./Pages/ErrorBoundary";
import Login from './Pages/Login.js';
import Logout from './Pages/Logout';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import TblGenericManager from './components/Admin/TblGenericManager';
import UserManager from './components/Admin/UserManager';
import MusicOnHoldManager from './components/Admin/MusicOnHoldManager';
import LogoutSync from './components/LogoutSync';
import SeoManager from './components/SeoManager';

// ✅ Make sure this file exists at src/session/SessionContext.js

import { SessionProvider } from './SessionContext';
import { SoftphoneProvider } from './contexts/SoftphoneContext';
import { ChatProvider } from './contexts/ChatContext';

const APP_TAB_LOCK_NAME = 'slacc-app-single-tab';
const APP_TAB_LEASE_KEY = 'slacc.app-tab-owner';
const APP_TAB_LEASE_MS = 5000;
const APP_TAB_HEARTBEAT_MS = 1500;

const readAppTabLease = () => {
    try {
        const lease = JSON.parse(localStorage.getItem(APP_TAB_LEASE_KEY) || 'null');
        if (!lease?.instanceId || !Number.isFinite(Number(lease.expiresAt))) return null;
        return lease;
    } catch {
        return null;
    }
};

const writeAppTabLease = (instanceId) => {
    try {
        localStorage.setItem(APP_TAB_LEASE_KEY, JSON.stringify({
            instanceId,
            expiresAt: Date.now() + APP_TAB_LEASE_MS
        }));
        return true;
    } catch {
        return false;
    }
};

const removeAppTabLease = (instanceId) => {
    try {
        if (readAppTabLease()?.instanceId === instanceId) {
            localStorage.removeItem(APP_TAB_LEASE_KEY);
        }
    } catch {
        // Ignore storage errors during page teardown.
    }
};

function SingleTabGuard({ children }) {
    const [lockState, setLockState] = useState('checking');
    const [lockAttempt, setLockAttempt] = useState(0);
    const instanceIdRef = useRef(
        window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`
    );

    useEffect(() => {
        let active = true;
        let releaseWebLock = null;
        let fallbackOwned = false;
        let heartbeatId = null;
        const instanceId = instanceIdRef.current;

        setLockState('checking');

        const loseFallbackOwnership = () => {
            fallbackOwned = false;
            if (heartbeatId) {
                clearInterval(heartbeatId);
                heartbeatId = null;
            }
            if (active) setLockState('blocked');
        };

        const renewFallbackLease = () => {
            const current = readAppTabLease();
            if (
                current &&
                current.instanceId !== instanceId &&
                Number(current.expiresAt) > Date.now()
            ) {
                loseFallbackOwnership();
                return;
            }
            writeAppTabLease(instanceId);
        };

        const claimFallbackLock = () => {
            const current = readAppTabLease();
            if (
                current &&
                current.instanceId !== instanceId &&
                Number(current.expiresAt) > Date.now()
            ) {
                setLockState('blocked');
                return;
            }

            if (!writeAppTabLease(instanceId) || readAppTabLease()?.instanceId !== instanceId) {
                setLockState('blocked');
                return;
            }

            fallbackOwned = true;
            setLockState('owner');
            heartbeatId = setInterval(renewFallbackLease, APP_TAB_HEARTBEAT_MS);
        };

        const handleFallbackStorage = (event) => {
            if (!fallbackOwned || event.key !== APP_TAB_LEASE_KEY) return;
            const current = readAppTabLease();
            if (
                current &&
                current.instanceId !== instanceId &&
                Number(current.expiresAt) > Date.now()
            ) {
                loseFallbackOwnership();
            }
        };

        const releaseFallbackLock = () => {
            if (fallbackOwned) removeAppTabLease(instanceId);
        };

        const acquireLock = async () => {
            if (navigator?.locks?.request) {
                try {
                    await navigator.locks.request(
                        APP_TAB_LOCK_NAME,
                        { mode: 'exclusive', ifAvailable: true },
                        async (lock) => {
                            if (!active) return;
                            if (!lock) {
                                setLockState('blocked');
                                return;
                            }
                            setLockState('owner');
                            await new Promise((resolve) => {
                                releaseWebLock = resolve;
                            });
                        }
                    );
                    return;
                } catch {
                    // Use the lease fallback when Web Locks are unavailable.
                }
            }

            if (active) claimFallbackLock();
        };

        window.addEventListener('storage', handleFallbackStorage);
        window.addEventListener('pagehide', releaseFallbackLock);
        acquireLock();

        return () => {
            active = false;
            releaseWebLock?.();
            if (heartbeatId) clearInterval(heartbeatId);
            releaseFallbackLock();
            window.removeEventListener('storage', handleFallbackStorage);
            window.removeEventListener('pagehide', releaseFallbackLock);
        };
    }, [lockAttempt]);

    if (lockState === 'checking') return null;

    if (lockState === 'blocked') {
        return (
            <main className="min-vh-100 d-flex align-items-center justify-content-center bg-light p-3">
                <div className="alert alert-warning mb-0 text-center" role="alert">
                    <h1 className="h5 mb-2">Application already open</h1>
                    <p className="mb-3">Close the other SLACC tab before continuing here.</p>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setLockAttempt((current) => current + 1)}
                    >
                        <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
                        Retry
                    </button>
                </div>
            </main>
        );
    }

    return children;
}

function App() {
    return (
        <SingleTabGuard>
            <SessionProvider>
                <SoftphoneProvider>
                    <ChatProvider> {/* Add Chat Provider */}
                        <ErrorBoundary>
                            <Router>
                                <LogoutSync />
                                <SeoManager />
                                <Routes>
                                {/* Default/Home Route - Regular Login */}
                                <Route path="/" element={<Login />} />

                                {/* Explicit Routes */}
                                <Route path="/login" element={<Login />} />
                                <Route
                                    path="/logout"
                                    element={<Logout />}
                                />
                                <Route
                                    path="/backoffice"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <Backoffice />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <Reports />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/dnis-statistics"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <DnisStatisticsReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/daily-call-report"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <DailyCallReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/agent-state-report"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <AgentStateReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/agent-login-logout-report"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <AgentLoginLogoutReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/agent-performance-report"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <AgentPerformanceReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/frontoffice"
                                    element={(
                                        <PrivateRoute>
                                            <Frontoffice />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/frontoffice-new"
                                    element={(
                                        <PrivateRoute>
                                            <FrontOfficeNew />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/frontoffice-shortcuts"
                                    element={(
                                        <PrivateRoute>
                                            <FrontOfficeShortcuts />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/admin/users"
                                    element={(
                                        <PrivateRoute allowRoles={['AD']}>
                                            <UserManager />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/admin/generic"
                                    element={(
                                        <PrivateRoute allowRoles={['AD']}>
                                            <TblGenericManager />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/agent-call-history"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <AgentCallHistoryReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/originator-by-disposition"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <OriginatorDispositionReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/historical/abandoned"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <OriginatorDispositionReport abandonedOnly />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/historical/callback-voicemail"
                                    element={(
                                        <PrivateRoute denyRoles={['FOE']}>
                                            <QueueContactOptionReport />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/reports/today-call-activity"
                                    element={(
                                        <PrivateRoute>
                                            <Navigate to="/frontoffice-new" replace />
                                        </PrivateRoute>
                                    )}
                                />
                                <Route
                                    path="/admin/music-on-hold"
                                    element={(
                                        <PrivateRoute allowRoles={['AD']}>
                                            <MusicOnHoldManager />
                                        </PrivateRoute>
                                    )}
                                />
                                </Routes>

                                {/* Route Debugger - Remove in production */}
                                {/* <RouteDebugger /> */}
                            </Router>

                        </ErrorBoundary>
                    </ChatProvider>
                </SoftphoneProvider>
            </SessionProvider>
        </SingleTabGuard>
    );
}

export default App;
